import * as React from 'react'
import { ReactElement } from 'react'
import { Card, Field, InfoLabel, makeStyles, mergeClasses, Title2, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import useEntity from '../../../../hooks/useEntity'
import Loading from '../../../../components/Loading'
import { ViewCRMProps } from '../ViewCRM'
import { FileFinanceMonitoringDto } from '../../../../pages/File/FinanceMonitoringOverview'
import MoneyText from '../../../../components/Text/Currency/MoneyText'
import { parseNumber } from '../../../../helpers/numberHelper'

const useStyles = makeStyles({
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalS,
        marginBottom: tokens.spacingVerticalS,

        '@media screen and (max-width: 800px)': {
            gridTemplateColumns: '1fr',
        },
    },
    summaryCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalM,
    },
    summaryCardClickable: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    container: {
        display: 'grid',
        margin: tokens.spacingHorizontalM,
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    containerVertical: {
        display: 'grid',
        margin: tokens.spacingHorizontalM,
        gridTemplateColumns: '1fr',
    },
    field: {
        display: 'grid',
        gridTemplateColumns: 'auto 90px',
        gap: `${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`,
    },
    fieldTotal: {
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke2,
        borderTopWidth: tokens.strokeWidthThin,
    },
})

export default function CRMFinanceTab(props: ViewCRMProps): ReactElement {
    const { t: translate } = useTranslation()
    const history = useHistory()
    const styles = useStyles()

    const [finance] = useEntity<FileFinanceMonitoringDto>(() => ({ url: `/crm/${props.crmRelationId}/finance` }), props.crmRelationId || false)

    if (!finance.loaded) {
        return <Loading />
    }

    const data = finance.data

    const onOpenInvoicesClick = () => {
        history.push(`/invoice?crmRelations=${props.crmRelationId}&statuses=DRAFT,FINAL,SETTLED,CREDITED`)
    }

    return (
        <div className={styles.summaryGrid}>
            <Card className={mergeClasses(styles.summaryCard, styles.summaryCardClickable)} onClick={onOpenInvoicesClick}>
                <Field
                    className={styles.field}
                    label={{
                        children: <InfoLabel info={translate('crm.finance.summary.open')}>{translate('crm.finance.summary.open')}</InfoLabel>,
                    }}
                >
                    <MoneyText
                        alpha={data?.outstandingInvoices?.currency || 'EUR'}
                        amount={data?.outstandingInvoices?.total || '0.00'}
                        textProps={{ size: 500, weight: 'semibold' }}
                        align={'spaceBetween'}
                    />
                </Field>
            </Card>
            <Card className={styles.summaryCard}>
                <Field className={styles.field} label={translate('crm.finance.summary.unbilled')}>
                    <MoneyText
                        alpha={data?.outstandingSubTotal?.currency || 'EUR'}
                        amount={data?.outstandingSubTotal?.total || '0.00'}
                        textProps={{ size: 500, weight: 'semibold' }}
                        align={'spaceBetween'}
                    />
                </Field>
            </Card>
            <Card className={styles.summaryCard}>
                <Title2>{translate('file.pivot.finance.monitoring.risk')}</Title2>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.hours')}>
                    <MoneyText
                        alpha={data?.outstandingHours?.currency || 'EUR'}
                        amount={data?.outstandingHours?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.disbursements')}>
                    <MoneyText
                        alpha={data?.outstandingDisbursements?.currency || 'EUR'}
                        amount={data?.outstandingDisbursements?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.fixedprices')}>
                    <MoneyText
                        alpha={data?.outstandingFixedPrices?.currency || 'EUR'}
                        amount={data?.outstandingFixedPrices?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.travels')}>
                    <MoneyText
                        alpha={data?.outstandingTravels?.currency || 'EUR'}
                        amount={data?.outstandingTravels?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field
                    className={mergeClasses(styles.field, styles.fieldTotal)}
                    label={<strong>{translate('file.pivot.finance.monitoring.outstanding.subtotal')}</strong>}
                >
                    <MoneyText
                        alpha={data?.outstandingSubTotal?.currency || 'EUR'}
                        amount={data?.outstandingSubTotal?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.vat')}>
                    <MoneyText
                        alpha={data?.outstandingSubTotal?.currency || 'EUR'}
                        amount={data?.outstandingSubTotal?.vat?.map(price => parseNumber(price.value, 'en')).reduce((a, b) => a + b, 0) || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field
                    className={mergeClasses(styles.field, styles.fieldTotal)}
                    label={<strong>{translate('file.pivot.finance.monitoring.outstanding.total')}</strong>}
                >
                    <MoneyText
                        alpha={data?.outstandingSubTotal?.currency || 'EUR'}
                        amount={data?.outstandingSubTotal?.total || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.invoices')}>
                    <MoneyText
                        alpha={data?.outstandingInvoices?.currency || 'EUR'}
                        amount={data?.outstandingInvoices?.total || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.outstanding.deposits')}>
                    <MoneyText
                        alpha={data?.outstandingDeposits?.currency || 'EUR'}
                        amount={data?.outstandingDeposits?.total || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field
                    className={mergeClasses(styles.field, styles.fieldTotal)}
                    label={<strong>{translate('file.pivot.finance.monitoring.outstanding.risktotal')}</strong>}
                >
                    <MoneyText
                        alpha={data?.outstandingTotal?.currency || 'EUR'}
                        amount={data?.outstandingTotal?.total || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
            </Card>
            <Card className={styles.summaryCard}>
                <Title2>{translate('file.pivot.finance.monitoring.turnover')}</Title2>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.billed.hours')}>
                    <MoneyText alpha={data?.billedHours?.currency || 'EUR'} amount={data?.billedHours?.subtotal || '0.00'} align={'spaceBetween'} />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.billed.disbursements')}>
                    <MoneyText
                        alpha={data?.billedDisbursements?.currency || 'EUR'}
                        amount={data?.billedDisbursements?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.billed.fixedprices')}>
                    <MoneyText
                        alpha={data?.billedFixedPrices?.currency || 'EUR'}
                        amount={data?.billedFixedPrices?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field className={styles.field} label={translate('file.pivot.finance.monitoring.billed.travels')}>
                    <MoneyText
                        alpha={data?.billedTravels?.currency || 'EUR'}
                        amount={data?.billedTravels?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field
                    className={mergeClasses(styles.field, styles.fieldTotal)}
                    label={<strong>{translate('file.pivot.finance.monitoring.billed.subtotal')}</strong>}
                >
                    <MoneyText
                        alpha={data?.billedSubTotal?.currency || 'EUR'}
                        amount={data?.billedSubTotal?.subtotal || '0.00'}
                        align={'spaceBetween'}
                    />
                </Field>
                <Field
                    className={mergeClasses(styles.field, styles.fieldTotal)}
                    label={<strong>{translate('file.pivot.finance.monitoring.total')}</strong>}
                >
                    <MoneyText alpha={data?.total?.currency || 'EUR'} amount={data?.total?.subtotal || '0.00'} align={'spaceBetween'} />
                </Field>
            </Card>
        </div>
    )
}
